import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons'
import { cn } from '@/lib/utils'
import useMedRequestFiltering from '@/lib/api/med-requests/filtering'
import {
  useCategoryConfig,
  useCuratedCategories,
  useSaveCategoryConfig,
  type CategoryConfigSection
} from '@/lib/api/med-requests/category-config'
import { resetFilterStore, useFilterStore } from '@/lib/stores/filter'
import { useEffect, useState, type ComponentPropsWithoutRef } from 'react'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { useCategoryConfigAccess } from '@/hooks/useCategoryConfigAccess'
import { useLocation } from 'react-router-dom'
import useInvestigationFiltering from '@/lib/api/investigations/filtering'
import { useGoNextInvestigation } from '@/hooks/useGoNextInvestigation'
import { useUser } from '@clerk/clerk-react'

const SHIPPING_TYPES_LABELS: Record<string, string> = {
  'Royal Mail Special Delivery Next Day by 1pm™ Signed For': 'Special Next Day',
  'Royal Mail 24 Tracked™': '24 Tracked'
  // 'Royal Mail 48 Tracked™': '48 Tracked',
  // 'Free Shipping': 'Free'
}

// Order-independent comparison of two category lists
const sameCategories = (a: Array<string>, b: Array<string>) =>
  a.length === b.length && a.every(category => b.includes(category))

function CategoryConfigDropdown({
  label,
  options,
  selected,
  onToggle
}: {
  label: string
  options: Array<string>
  selected: Array<string>
  onToggle: (category: string) => void
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={selected.length ? 'toggleOn' : 'toggleOff'}>
          {label}
          {selected.length ? ` (${selected.length})` : ''}
          <ChevronDownIcon className='ml-1 h-4 w-4' />
        </Button>
      </PopoverTrigger>

      <PopoverContent align='start' className='w-72 p-0'>
        <Command>
          <CommandInput placeholder='Search categories...' />
          <CommandList>
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              {options.map(category => (
                <CommandItem key={category} value={category} onSelect={() => onToggle(category)}>
                  <CheckIcon
                    className={cn('mr-2 h-4 w-4', selected.includes(category) ? 'opacity-100' : 'opacity-0')}
                  />
                  {category}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Saved, role-scoped category config (not a live queue filter). LEAD_PRESCRIBER edits the
// `prescribers` section, CS_PHARMACY_MANAGER edits `pharmacists`; the backend authorises the section.
function CategoryConfigEditor({ section }: { section: CategoryConfigSection }) {
  const { data, isPending } = useCategoryConfig()
  const save = useSaveCategoryConfig()

  const [included, setIncluded] = useState<Array<string>>([])
  const [excluded, setExcluded] = useState<Array<string>>([])

  useEffect(() => {
    if (!data) {
      return
    }
    const saved = data?.config[section] ?? { included: [], excluded: [] }
    setIncluded(saved.included)
    setExcluded(saved.excluded)
  }, [data, section])

  const options = data?.options ?? []
  if (!options.length) {
    return null
  }

  const saved = data?.config[section] ?? { included: [], excluded: [] }
  const isUnchanged = sameCategories(included, saved.included) && sameCategories(excluded, saved.excluded)

  const toggle = (setSelected: typeof setIncluded) => (category: string) => {
    setSelected(selected =>
      selected.includes(category) ? selected.filter(entry => entry !== category) : [...selected, category]
    )
  }

  return (
    <>
      <div className='min-h-px w-full bg-[#E9EAED]' />

      <div className='flex items-center gap-4'>
        <h3 className='font-poppins text-sm leading-5'>Categories:</h3>

        <CategoryConfigDropdown
          label='Included Categories'
          options={options}
          selected={included}
          onToggle={toggle(setIncluded)}
        />

        <CategoryConfigDropdown
          label='Excluding Categories'
          options={options}
          selected={excluded}
          onToggle={toggle(setExcluded)}
        />

        <Button
          type='button'
          disabled={isPending || save.isPending || isUnchanged}
          onClick={() => save.mutate({ included, excluded })}
        >
          {save.isPending ? 'Saving…' : 'Save config'}
        </Button>
      </div>
    </>
  )
}

function ToggleButton({
  children,
  onToggle,
  toggle = false
}: {
  onToggle: (value: boolean) => void
  toggle?: boolean
} & ComponentPropsWithoutRef<'button'>) {
  return (
    <Button variant={toggle ? 'toggleOn' : 'toggleOff'} onClick={() => onToggle(!toggle)}>
      {children}
    </Button>
  )
}

const { setFilteringOptions, updateFilteringOptions } = useFilterStore.getState()

// Non-manager view: the lead-curated categories shown as selectable chips that drive the live
// queue filter (the existing categories / excludedCategories filter). Hidden when the lead has
// curated nothing for this role.
function CuratedCategoriesRow() {
  const { data } = useCuratedCategories()
  const { filteringOptions } = useFilterStore()

  const included = data?.included ?? []
  const excluded = data?.excluded ?? []

  if (!included.length && !excluded.length) {
    return null
  }

  const selectedIncluded = filteringOptions.categories ?? []
  const selectedExcluded = filteringOptions.excludedCategories ?? []

  const toggleCategory = (key: 'categories' | 'excludedCategories', category: string) =>
    updateFilteringOptions(options => {
      const otherKey = key === 'categories' ? 'excludedCategories' : 'categories'
      const selected = options[key] ?? []
      const isSelected = selected.includes(category)
      return {
        ...options,
        [key]: isSelected ? selected.filter(entry => entry !== category) : [...selected, category],
        // A category can't be both included and excluded: selecting it on one side clears it from the other.
        [otherKey]: isSelected
          ? (options[otherKey] ?? [])
          : (options[otherKey] ?? []).filter(entry => entry !== category),
        investigations: false
      }
    })

  return (
    <>
      <div className='min-h-px w-full bg-[#E9EAED]' />

      <div className='flex flex-wrap items-center gap-4'>
        <h3 className='font-poppins text-sm leading-5'>Curated category filters:</h3>

        {!!included.length && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Include:</span>
            {included.map(category => (
              <ToggleButton
                key={category}
                toggle={selectedIncluded.includes(category)}
                onToggle={() => toggleCategory('categories', category)}
              >
                {category}
              </ToggleButton>
            ))}
          </div>
        )}

        {!!excluded.length && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Exclude:</span>
            {excluded.map(category => (
              <ToggleButton
                key={category}
                toggle={selectedExcluded.includes(category)}
                onToggle={() => toggleCategory('excludedCategories', category)}
              >
                {category}
              </ToggleButton>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default function Filter() {
  const { user } = useUser()
  const roles = user?.publicMetadata.roles ?? []
  const isJuniorPrescriber = roles.includes('JUNIOR_PRESCRIBER')

  const { categoryConfigSection } = useCategoryConfigAccess()

  const { filteringOptions } = useFilterStore()
  const hasInvestigationFilter = filteringOptions.investigations

  const { isPending: investigationsPending, data: investigations } = useInvestigationFiltering(!isJuniorPrescriber)
  const { isPending: medRequestsPending, data: medRequestsData } = useMedRequestFiltering(!hasInvestigationFilter)

  const total = hasInvestigationFilter ? investigations?.length : medRequestsData?.total
  const isPending = hasInvestigationFilter ? investigationsPending : medRequestsPending

  const goNextMedRequest = useGoNextMedRequest(!hasInvestigationFilter)
  const goNextInvestigation = useGoNextInvestigation(hasInvestigationFilter && !isJuniorPrescriber)
  const goNextRequest = hasInvestigationFilter ? goNextInvestigation : goNextMedRequest

  const location = useLocation()
  useEffect(() => {
    resetFilterStore()
  }, [location])

  return (
    <div className='px-10 py-8 rounded-2xl border border-custom-neutral-700 bg-white flex flex-col gap-6'>
      <div className='flex items-center gap-4'>
        <h3 className='font-poppins text-sm leading-5'>Shipping Type:</h3>

        <div className='flex items-center gap-2'>
          {Object.values(SHIPPING_TYPES_LABELS).map((label, index) => (
            <ToggleButton
              key={index}
              toggle={filteringOptions['Shipping Method'].includes(Object.keys(SHIPPING_TYPES_LABELS)[index])}
              onToggle={isOn => {
                const { filteringOptions } = useFilterStore.getState()

                const labelKey = Object.keys(SHIPPING_TYPES_LABELS).find(
                  key => SHIPPING_TYPES_LABELS[key as string] === label
                )
                if (!labelKey) {
                  return
                }

                let existingFilterData = filteringOptions['Shipping Method']
                if (isOn) {
                  existingFilterData = [...existingFilterData, labelKey]
                } else {
                  existingFilterData = existingFilterData.filter(key => key !== labelKey)
                }

                setFilteringOptions({
                  ...filteringOptions,
                  'Shipping Method': [...new Set(existingFilterData)],
                  investigations: false
                })
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </div>
      </div>

      <div className='min-h-px w-full bg-[#E9EAED]' />

      <div className='flex items-center gap-4'>
        <h3 className='font-poppins text-sm leading-5'>Advanced Filters:</h3>

        <ToggleButton
          toggle={filteringOptions.priority}
          onToggle={isOn =>
            updateFilteringOptions(filteringOptions => ({
              ...filteringOptions,
              priority: !!isOn,
              // Priority excludes anything ever put on hold, so it can't combine with On Holds.
              onHolds: isOn ? false : filteringOptions.onHolds,
              investigations: false
            }))
          }
        >
          Priority
        </ToggleButton>

        <ToggleButton
          toggle={filteringOptions['ID Verified Time'].length > 0}
          onToggle={isOn =>
            updateFilteringOptions(filteringOptions => ({
              ...filteringOptions,
              'ID Verified Time': isOn ? ['after'] : [],
              investigations: false
            }))
          }
        >
          Before Cutoff
        </ToggleButton>

        <ToggleButton
          toggle={filteringOptions['onHolds']}
          onToggle={isOn =>
            updateFilteringOptions(filteringOptions => ({
              ...filteringOptions,
              onHolds: !!isOn,
              priority: isOn ? false : filteringOptions.priority,
              investigations: false
            }))
          }
        >
          On Holds
        </ToggleButton>

        {!isJuniorPrescriber && (
          <ToggleButton
            toggle={filteringOptions.investigations}
            onToggle={isOn => {
              resetFilterStore()
              updateFilteringOptions(filteringOptions => ({
                ...filteringOptions,
                investigations: !!isOn
              }))
            }}
          >
            Investigations
          </ToggleButton>
        )}
      </div>

      <CuratedCategoriesRow />

      {categoryConfigSection && <CategoryConfigEditor section={categoryConfigSection} />}

      <Button
        type='button'
        data-testid='view-all'
        disabled={isPending || !total}
        className='self-center mt-6'
        onClick={() => goNextRequest()}
      >
        {isPending ? 'Loading' : total || 'No'} {hasInvestigationFilter ? 'investigations' : 'medication requests'}
        {isPending && '...'}
      </Button>
    </div>
  )
}
